import * as d3 from 'd3';
import { type CycledEnergyMeasurementRecord } from '../../../../services/battery-monitoring-api/types/battery-monitoring-api';
import { CHART_COLORS, renderNotAvailable, applyAxisStyleX, applyAxisStyleY } from '../../../utils/chart-helpers';

export interface DrawCycledEnergyChartProperties {
  containerElement: HTMLDivElement;
  svgElement: SVGSVGElement;
  records: CycledEnergyMeasurementRecord[];
}

const defaultChartOuterWidth = 744;
const minChartOuterHeight = 238;
const minChartWidth = 120;
const minChartHeight = 80;

const drawCycledEnergyChart = ({ containerElement, svgElement, records }: DrawCycledEnergyChartProperties): void => {
  const svg = d3.select(svgElement);
  const { width, height } = containerElement.getBoundingClientRect();

  const innerWidth = width || defaultChartOuterWidth;
  const innerHeight = height || minChartOuterHeight;

  const margin = { top: 32, right: 0, bottom: 20, left: 34 };
  const chartWidth = Math.max(innerWidth - margin.left - margin.right, minChartWidth);
  const chartHeight = Math.max(innerHeight - margin.top - margin.bottom, minChartHeight);

  svg.selectAll('*').remove();

  const hasData = records.length > 0;

  const parsedData = hasData
    ? records
        .map((record) => ({
          date: new Date(record.readoutTimestamp),
          value: record.cycledEnergyPerKm,
        }))
        .toSorted((a, b) => a.date.getTime() - b.date.getTime())
    : [];

  const g = svg
    .attr('viewBox', `0 0 ${innerWidth} ${innerHeight}`)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  const xScale = hasData
    ? d3
        .scalePoint<number>()
        .domain(parsedData.map((_, index) => index))
        .range([0, chartWidth])
        .padding(0.5)
    : d3.scalePoint<number>().domain([0, 1, 2]).range([0, chartWidth]).padding(0.5);

  const yMax = hasData ? (d3.max(parsedData, (d) => d.value) ?? 0) : 1;
  const yScale = d3
    .scaleLinear()
    .domain(hasData ? [0, yMax * 1.15] : [0, 1])
    .nice()
    .range([chartHeight, 0]);

  const xAxis = hasData
    ? d3
        .axisBottom<number>(xScale)
        .tickValues(parsedData.map((_, index) => index))
        .tickFormat((index) => d3.timeFormat("%b'%y")(parsedData[index].date))
    : d3
        .axisBottom<number>(xScale)
        .tickValues([0, 1, 2])
        .tickFormat((d) => {
          const now = new Date();
          const date = new Date(now.getFullYear(), now.getMonth() - 2 + d, 1);
          return d3.timeFormat("%b'%y")(date);
        });

  const yAxis = d3.axisLeft<number>(yScale).ticks(3);

  g.append('g')
    .call(
      d3
        .axisLeft(yScale)
        .ticks(3)
        .tickSize(-chartWidth)
        .tickFormat(() => ''),
    )
    .call((gAxis) => gAxis.select('.domain').remove())
    .selectAll('line')
    .attr('stroke', CHART_COLORS.grid)
    .attr('stroke-opacity', 0.25);

  const yAxisGroup = g.append('g').call(yAxis);
  applyAxisStyleY(yAxisGroup);

  const xAxisGroup = g.append('g').attr('transform', `translate(0,${chartHeight})`).call(xAxis);
  applyAxisStyleX(xAxisGroup);

  if (!hasData) {
    renderNotAvailable(g, chartWidth, chartHeight);
    return;
  }

  const line = d3
    .line<(typeof parsedData)[number]>()
    .x((_, index) => xScale(index) ?? 0)
    .y((d) => yScale(d.value))
    .curve(d3.curveMonotoneX);

  g.append('path')
    .datum(parsedData)
    .attr('fill', 'none')
    .attr('stroke', CHART_COLORS.line)
    .attr('stroke-width', 2)
    .attr('d', line);

  g.selectAll('circle')
    .data(parsedData)
    .enter()
    .append('circle')
    .attr('cx', (_, index) => xScale(index) ?? 0)
    .attr('cy', (d) => yScale(d.value))
    .attr('r', 4)
    .attr('fill', CHART_COLORS.pointFill)
    .attr('stroke', CHART_COLORS.pointStroke)
    .attr('stroke-width', 2);
};

export default drawCycledEnergyChart;
